// Chemin: C:\gestion-copro\src\types\signalement.types.ts
// Types et constantes pour les signalements ORION PILOT
// Session 15 : ajout champ affaireId
// Session 212 : ajout coproprieteId (étanchéité inter-copropriétés)

import { Timestamp } from 'firebase/firestore';

// ============================================
// TYPES ÉNUMÉRÉS
// ============================================

// Types de localisation
export type Localisation = 'parties_communes' | 'parties_privatives' | 'equipements';
export type SignalementLocalisation = Localisation; // Alias pour compatibilité

// Niveaux d'urgence
export type Urgence = 'basse' | 'moyenne' | 'haute' | 'critique';
export type SignalementUrgence = Urgence; // Alias pour compatibilité

// Statuts possibles d'un signalement
export type StatutSignalement = 
  | 'nouveau' 
  | 'en_cours' 
  | 'transmis_syndic' 
  | 'transmis_promoteur' 
  | 'resolu' 
  | 'refuse';
export type SignalementStatut = StatutSignalement; // Alias pour compatibilité

// ============================================
// CONSTANTES - LABELS ET COULEURS
// ============================================

// Labels pour les localisations
export const LOCALISATION_LABELS: Record<Localisation, string> = {
  parties_communes: 'Parties communes',
  parties_privatives: 'Parties privatives',
  equipements: 'Équipements'
};

// Labels pour les urgences
export const URGENCE_LABELS: Record<Urgence, string> = {
  basse: 'Basse',
  moyenne: 'Moyenne',
  haute: 'Haute',
  critique: 'Critique'
};

// Couleurs pour les urgences (classes Tailwind)
export const URGENCE_COLORS: Record<Urgence, string> = {
  basse: 'bg-green-100 text-green-800',
  moyenne: 'bg-yellow-100 text-yellow-800',
  haute: 'bg-orange-100 text-orange-800',
  critique: 'bg-red-100 text-red-800'
};

// Labels pour les statuts
export const STATUT_LABELS: Record<StatutSignalement, string> = {
  nouveau: 'Nouveau',
  en_cours: 'En cours',
  transmis_syndic: 'Transmis au syndic',
  transmis_promoteur: 'Transmis au promoteur',
  resolu: 'Résolu',
  refuse: 'Refusé'
};

// Couleurs pour les statuts (classes Tailwind)
export const STATUT_COLORS: Record<StatutSignalement, string> = {
  nouveau: 'bg-red-100 text-red-800',
  en_cours: 'bg-yellow-100 text-yellow-800',
  transmis_syndic: 'bg-blue-100 text-blue-800',
  transmis_promoteur: 'bg-purple-100 text-purple-800',
  resolu: 'bg-green-100 text-green-800',
  refuse: 'bg-gray-100 text-gray-800'
};

// Catégories de problèmes
export const CATEGORIES: string[] = [
  'Infiltration / Humidité',
  'Fissure / Fêlure',
  'Peinture / Revêtement',
  'Menuiserie (portes, fenêtres)',
  'Plomberie',
  'Électricité',
  'Ascenseur',
  'Parties communes extérieures',
  'Chauffage / VMC',
  'Autre'
];

// ============================================
// INTERFACES
// ============================================

// Informations du déclarant
export interface Declarant {
  nom: string;
  email?: string;
  telephone?: string;
  lot?: string;
  coproprietaireId?: string;
}

// Entrée dans l'historique du signalement
export interface HistoriqueEntry {
  date: Timestamp;
  action: string;
  details?: string;
  auteur?: string;
}

// Document signalement complet (stocké dans Firestore)
export interface Signalement {
  id: string;

  // Isolation par copropriété (obligatoire)
  coproprieteId: string;
  
  // Numéro de référence (ex: SIG-20260121-001)
  numeroSignalement?: string;
  
  // Informations déclarant
  declarant: Declarant;

  // Identifiant du déclarant (VIGIE)
  declarantId?: string;
  
  // Description du problème
  titre: string;
  description: string;
  
  // Localisation
  localisation: Localisation;
  localisationDetail: string;
  
  // Classification
  categorie: string;
  urgence: Urgence;
  
  // Médias
  photos: string[];  // URLs Firebase Storage
  
  // Suivi
  statut: StatutSignalement;
  historique: HistoriqueEntry[];
  
  // GPA
  lieeGPA?: boolean;
  reserveId?: string;
  
  // Lien avec une affaire (module Affaires)
  affaireId?: string;
  
  // Métadonnées
  dateSignalement: Timestamp;
  dateModification?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// ============================================
// TYPES POUR LES FORMULAIRES
// ============================================

// Données du formulaire de création
export interface SignalementFormData {
  // Isolation par copropriété (obligatoire)
  coproprieteId: string;

  // Déclarant
  nom: string;
  email: string;
  telephone: string;
  lot: string;
  
  // Description
  titre: string;
  description: string;
  
  // Localisation
  localisation: Localisation | '';
  localisationDetail: string;
  
  // Classification
  categorie: string;
  urgence: Urgence | '';
  
  // Photos (fichiers avant upload)
  photos: File[];
}

// Valeurs initiales du formulaire
export const INITIAL_FORM_DATA: Omit<SignalementFormData, 'coproprieteId'> = {
  nom: '',
  email: '',
  telephone: '',
  lot: '',
  titre: '',
  description: '',
  localisation: '',
  localisationDetail: '',
  categorie: '',
  urgence: '',
  photos: []
};

// Données pour créer un signalement (envoyées au service)
export interface CreateSignalementData {
  declarant: Declarant;
  titre: string;
  description: string;
  localisation: Localisation;
  localisationDetail: string;
  categorie: string;
  urgence: Urgence;
  photos: string[];  // URLs après upload
}

// Résultat de la création d'un signalement
export interface CreateSignalementResult {
  success: boolean;
  signalementId?: string;
  numeroSignalement?: string;
  error?: string;
}
